import React, { useEffect, useRef } from 'react';
import { useMapStore } from '../stores/useMapStore';
import type { Map as MapLibreMap } from 'maplibre-gl';

interface PolygonDrawProps {
  mapRef: React.RefObject<MapLibreMap | null>;
  mapLoaded: boolean;
}

/** Sort vertices radially around centroid to prevent self-intersections */
function sortVerticesRadially(points: [number, number][]): [number, number][] {
  if (points.length < 3) return points;

  let sumLng = 0;
  let sumLat = 0;
  points.forEach(([lng, lat]) => {
    sumLng += lng;
    sumLat += lat;
  });
  const cLng = sumLng / points.length;
  const cLat = sumLat / points.length;

  return [...points].sort((a, b) => {
    const angleA = Math.atan2(a[1] - cLat, a[0] - cLng);
    const angleB = Math.atan2(b[1] - cLat, b[0] - cLng);
    return angleA - angleB;
  });
}

/** Compute bbox from polygon vertices */
function computeBboxFromPolygon(vertices: [number, number][]): [number, number, number, number] | null {
  if (vertices.length < 3) return null;
  const lons = vertices.map((v) => v[0]);
  const lats = vertices.map((v) => v[1]);
  return [Math.min(...lons), Math.min(...lats), Math.max(...lons), Math.max(...lats)];
}

export const PolygonDraw: React.FC<PolygonDrawProps> = ({ mapRef, mapLoaded }) => {
  const selectionMode = useMapStore((s) => s.selectionMode);
  const polygonVertices = useMapStore((s) => s.polygonVertices);
  const setPolygonVertices = useMapStore((s) => s.setPolygonVertices);
  const setBbox = useMapStore((s) => s.setBbox);

  const verticesRef = useRef(polygonVertices);
  verticesRef.current = polygonVertices;

  // Add GeoJSON source/layers when map loads
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;

    // Wait for style to be ready
    const setup = () => {
      if (map.getSource('drawn-polygon')) return;

      map.addSource('drawn-polygon', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        },
      });

      map.addLayer({
        id: 'polygon-fill',
        type: 'fill',
        source: 'drawn-polygon',
        paint: {
          'fill-color': '#06b6d4',
          'fill-opacity': 0.15,
        },
        filter: ['==', '$type', 'Polygon'],
      });

      map.addLayer({
        id: 'polygon-stroke',
        type: 'line',
        source: 'drawn-polygon',
        paint: {
          'line-color': '#06b6d4',
          'line-width': 2,
        },
        filter: ['in', '$type', 'Polygon', 'LineString'],
      });

      map.addLayer({
        id: 'polygon-points',
        type: 'circle',
        source: 'drawn-polygon',
        paint: {
          'circle-radius': 6,
          'circle-color': '#22d3ee',
          'circle-stroke-color': '#0f172a',
          'circle-stroke-width': 2,
        },
        filter: ['==', '$type', 'Point'],
      });
    };

    if (map.isStyleLoaded()) {
      setup();
    } else {
      map.once('style.load', setup);
    }
  }, [mapRef, mapLoaded]);

  // Update GeoJSON source when vertices change
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;

    const source = map.getSource('drawn-polygon') as
      | import('maplibre-gl').GeoJSONSource
      | undefined;
    if (!source) return;

    const features: GeoJSON.Feature[] = [];

    // Add points for vertices
    polygonVertices.forEach((vertex) => {
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: vertex },
        properties: {},
      });
    });

    if (polygonVertices.length >= 2) {
      features.push({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [...polygonVertices, polygonVertices[0]],
        },
        properties: {},
      });
    }

    if (polygonVertices.length >= 3) {
      features.push({
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [[...polygonVertices, polygonVertices[0]]],
        },
        properties: {},
      });
    }

    source.setData({
      type: 'FeatureCollection',
      features,
    });

    // Update bbox from polygon
    const bbox = computeBboxFromPolygon(polygonVertices);
    if (bbox) setBbox(bbox);
  }, [polygonVertices, mapRef, mapLoaded, setBbox]);

  // Handle Escape key to clear polygon
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectionMode === 'polygon' && polygonVertices.length > 0) {
        setPolygonVertices([]);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selectionMode, polygonVertices, setPolygonVertices]);

  // Handle map clicks for polygon drawing
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;

    const handleClick = (e: import('maplibre-gl').MapMouseEvent) => {
      if (selectionMode !== 'polygon') return;
      const coords: [number, number] = [e.lngLat.lng, e.lngLat.lat];
      const newVertices = sortVerticesRadially([...verticesRef.current, coords]);
      setPolygonVertices(newVertices);
    };

    const handleDblClick = () => {
      if (selectionMode !== 'polygon') return;
      if (verticesRef.current.length >= 3) {
        // Polygon is complete — user can now analyze
        // Don't auto-clear, let them see the result
      }
    };

    map.on('click', handleClick);
    map.on('dblclick', handleDblClick);

    return () => {
      map.off('click', handleClick);
      map.off('dblclick', handleDblClick);
    };
  }, [mapRef, mapLoaded, selectionMode, setPolygonVertices]);

  // Clean up MapLibre layers/source on unmount or mode change
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;

    return () => {
      if (selectionMode !== 'polygon') {
        try {
          if (map.getLayer('polygon-fill')) map.removeLayer('polygon-fill');
          if (map.getLayer('polygon-stroke')) map.removeLayer('polygon-stroke');
          if (map.getLayer('polygon-points')) map.removeLayer('polygon-points');
          if (map.getSource('drawn-polygon')) map.removeSource('drawn-polygon');
        } catch {
          // ignore cleanup errors
        }
      }
    };
  }, [mapRef, mapLoaded, selectionMode, setPolygonVertices]);

  // Update cursor based on mode
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;

    const canvas = map.getCanvas();
    if (selectionMode === 'polygon') {
      canvas.style.cursor = 'crosshair';
    } else {
      canvas.style.cursor = '';
    }
  }, [mapRef, mapLoaded, selectionMode]);

  if (selectionMode !== 'polygon') return null;

  return (
    <div className="absolute bottom-4 left-1/2 z-10 -translate-x-1/2">
      <div className="flex items-center gap-2 rounded-lg bg-slate-800/90 px-3 py-2 backdrop-blur-sm border border-slate-600/50 shadow-lg">
        <span className="text-xs text-slate-300">
          {polygonVertices.length === 0
            ? 'Click on the map to start drawing'
            : polygonVertices.length < 3
              ? `${polygonVertices.length} point${polygonVertices.length > 1 ? 's' : ''} — keep clicking`
              : `${polygonVertices.length} points — double-click to finish`}
        </span>
        {polygonVertices.length > 0 && (
          <button
            onClick={() => setPolygonVertices([])}
            className="text-xs text-red-400 hover:text-red-300 transition-colors"
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
};
